'use strict';

/**
 * Certification kinds + per-type configuration — shared, pure data/helpers (no pict / DOM / network) so the
 * #/Certifications workspace and the #/CertificationTypes management screen use ONE source of truth (mirrors the
 * Permission-Catalog / Task-Catalog convention).
 *
 * The holder poly-join (CertificationEntityPolyJoin) is polymorphic: a Certification can be held by a User, Material,
 * Lab, Equipment, Test, MixDesign, … HOLDER_KINDS knows how to list/search/label each (list route = Scope+'s', single
 * = Scope). `Certifiable` kinds are offered when certifying; every kind (plus a generic fallback) resolves existing
 * holders.
 *
 * Each CertificationType carries a small per-type CONFIG in the cascading Configuration service — namespace
 * CERT_CONFIG_NAMESPACE, ONE NamespaceValue per type (`CertificationType-<id>`), blob { HolderKinds, AppliesToKinds,
 * DefaultValidityYears }. It declares which kinds a type certifies (holders) and certifies-for, so the workspace can
 * pre-scope + simplify. Absent config falls back to built-in defaults (defaultConfigFor) — most certifications certify
 * PEOPLE for materials/labs; lab/plant-flavored ones default to Labs.
 */

// `Search` is the columns a kind is found by; Users use the shared user search instead (access/User-Search.js).
const HOLDER_KINDS =
[
	{ Kind: 'User', Label: 'User', One: 'User', Plural: 'Users', IDField: 'IDUser', Certifiable: true, Search: null, Ph: 'users by name, login ID or email' },
	{ Kind: 'Material', Label: 'Material', One: 'Material', Plural: 'Materials', IDField: 'IDMaterial', Certifiable: true, Search: [ 'Name' ], Ph: 'materials' },
	{ Kind: 'Lab', Label: 'Lab', One: 'Lab', Plural: 'Labs', IDField: 'IDLab', Certifiable: true, Search: [ 'Name' ], Ph: 'labs' },
	{ Kind: 'Equipment', Label: 'Equipment', One: 'Equipment', Plural: 'Equipments', IDField: 'IDEquipment', Certifiable: true, Search: [ 'Name' ], Ph: 'equipment' },
	{ Kind: 'Test', Label: 'Test', One: 'Test', Plural: 'Tests', IDField: 'IDTest', Certifiable: true, Search: [ 'Name' ], Ph: 'tests' },
	{ Kind: 'Product', Label: 'Product', One: 'Product', Plural: 'Products', IDField: 'IDProduct', Certifiable: true, Search: [ 'Name' ], Ph: 'products' },
	{ Kind: 'Organization', Label: 'Organization', One: 'Organization', Plural: 'Organizations', IDField: 'IDOrganization', Certifiable: true, Search: [ 'Name' ], Ph: 'organizations' },
	{ Kind: 'PayItem', Label: 'Pay Item', One: 'PayItem', Plural: 'PayItems', IDField: 'IDPayItem', Certifiable: false, Search: [ 'Name' ], Ph: 'pay items' },
	{ Kind: 'MixDesign', Label: 'Mix Design', One: 'MixDesign', Plural: 'MixDesigns', IDField: 'IDMixDesign', Certifiable: false, Search: [ 'Name' ], Ph: 'mix designs' },
];
const HOLDER_KIND_BY_TYPE = HOLDER_KINDS.reduce((pAcc, pKind) => { pAcc[pKind.Kind] = pKind; return pAcc; }, {});

// Kinds that can be CERTIFIED (holders) and kinds a type can CERTIFY-FOR — the option lists for the two dropdowns.
const HOLDER_CERTIFIABLE_LIST = HOLDER_KINDS.filter((pKind) => pKind.Certifiable).map((pKind) => pKind.Kind);
const APPLIES_TO_KIND_LIST = [ 'Material', 'Lab', 'Test', 'Equipment', 'Product', 'Organization', 'PayItem' ];

// Configuration service: one blob per CertificationType.
const CERT_CONFIG_NAMESPACE = 'CertificationTypeConfig';
const configNamespaceValue = (pIDCertificationType) => `CertificationType-${Number(pIDCertificationType)}`;

const DEFAULT_HOLDER_KINDS = [ 'User' ];
const DEFAULT_APPLIES_KINDS = [ 'Material', 'Lab' ];

/** Built-in defaults for a type that has no saved config yet — lab/plant-flavored types default to certifying Labs. */
function defaultConfigFor(pType)
{
	const tmpText = `${(pType && pType.Name) || ''} ${(pType && pType.Verb) || ''} ${(pType && pType.Hash) || ''}`.toLowerCase();
	const tmpLabbish = /\b(lab|laborator|plant|facility|batch)\b/.test(tmpText);
	return {
		HolderKinds: tmpLabbish ? [ 'Lab' ] : DEFAULT_HOLDER_KINDS.slice(),
		AppliesToKinds: DEFAULT_APPLIES_KINDS.slice(),
		DefaultValidityYears: 0,
	};
}

/** Merge a stored (possibly partial/empty) config over the type's defaults; sanitize the kind lists. */
function normalizeConfig(pRaw, pType)
{
	const tmpDefault = defaultConfigFor(pType);
	const tmpRaw = (pRaw && (typeof pRaw === 'object')) ? pRaw : {};
	const fClean = (pArr, pAllowed, pFallback) =>
	{
		if (!Array.isArray(pArr)) { return pFallback.slice(); }
		const tmpClean = pArr.filter((pKind) => pAllowed.indexOf(pKind) > -1);
		return tmpClean.length ? tmpClean : pFallback.slice();
	};
	const tmpConfigured = !!(pRaw && (Array.isArray(pRaw.HolderKinds) || Array.isArray(pRaw.AppliesToKinds) || (pRaw.DefaultValidityYears != null)));
	return {
		HolderKinds: fClean(tmpRaw.HolderKinds, HOLDER_CERTIFIABLE_LIST, tmpDefault.HolderKinds),
		AppliesToKinds: fClean(tmpRaw.AppliesToKinds, APPLIES_TO_KIND_LIST, tmpDefault.AppliesToKinds),
		DefaultValidityYears: (Number(tmpRaw.DefaultValidityYears) >= 0) ? Number(tmpRaw.DefaultValidityYears) : tmpDefault.DefaultValidityYears,
		Configured: tmpConfigured,
	};
}

// Suggested starter certification types — offered to a customer whose catalog is empty (the #/CertificationTypes
// "get started" panel). Adopting one creates the real CertificationType record and applies the bundled config, so a
// fresh tenant gets a running start instead of a blank list. Curated from the common DOT materials-QA credentials
// (mostly people certified for tests/materials; includes a Lab-held example to show the polymorphism).
const STARTER_TYPES =
[
	{ Name: 'Concrete Field Testing Technician', Verb: 'Test Concrete', Hash: 'CONC-FIELD', Description: 'Field tests on fresh concrete — slump, air content, temperature, and casting cylinders.', HolderKinds: [ 'User' ], AppliesToKinds: [ 'Test', 'Material' ], DefaultValidityYears: 5 },
	{ Name: 'Asphalt Plant & Field Technician', Verb: 'Sample Asphalt', Hash: 'HMA-TECH', Description: 'Sampling and plant/field testing of hot-mix asphalt.', HolderKinds: [ 'User' ], AppliesToKinds: [ 'Material', 'Test' ], DefaultValidityYears: 5 },
	{ Name: 'Nuclear Density Gauge Operator', Verb: 'Operate Gauge', Hash: 'NUC-GAUGE', Description: 'Operate a nuclear moisture/density gauge for in-place density testing.', HolderKinds: [ 'User' ], AppliesToKinds: [ 'Equipment', 'Test' ], DefaultValidityYears: 2 },
	{ Name: 'Aggregate Sampling & Testing', Verb: 'Sample Aggregate', Hash: 'AGG-TECH', Description: 'Sampling and gradation/quality testing of aggregates.', HolderKinds: [ 'User' ], AppliesToKinds: [ 'Material', 'Test' ], DefaultValidityYears: 5 },
	{ Name: 'Soils & Embankment Field Technician', Verb: 'Test Soils', Hash: 'SOIL-TECH', Description: 'Field density, moisture, and Proctor testing of soils and embankment.', HolderKinds: [ 'User' ], AppliesToKinds: [ 'Material', 'Test' ], DefaultValidityYears: 5 },
	{ Name: 'Qualified Testing Laboratory', Verb: 'Qualify Lab', Hash: 'LAB-QUAL', Description: 'A laboratory qualified to perform specified material tests.', HolderKinds: [ 'Lab' ], AppliesToKinds: [ 'Test', 'Material' ], DefaultValidityYears: 3 },
];

module.exports =
{
	HOLDER_KINDS, HOLDER_KIND_BY_TYPE, HOLDER_CERTIFIABLE_LIST, APPLIES_TO_KIND_LIST,
	CERT_CONFIG_NAMESPACE, configNamespaceValue,
	defaultConfigFor, normalizeConfig, STARTER_TYPES,
};
